<template>
  <AppShell
    :role-label="navRoleLabel"
    :nav-items="navItems"
    active-view="records"
    :account="auth.user || {}"
    :collapsed="navCollapsed"
    @navigate="openNav"
    @logout="signOut"
    @toggle-nav="navCollapsed = !navCollapsed"
  >
    <div class="record-workspace">
      <section v-if="recordLoading" class="record-state-panel" role="status" aria-live="polite">
        <div class="section-kicker">{{ navRoleLabel }}</div>
        <h2>Loading record...</h2>
      </section>
      <section v-else-if="recordError" class="record-state-panel">
        <div class="section-kicker">{{ navRoleLabel }}</div>
        <h2>Record unavailable</h2>
        <p>{{ recordError }}</p>
        <button class="ui-button ui-button--primary" type="button" @click="openNav('records')">Back to Writing Records</button>
      </section>
      <WritingRecordDetail
        v-else-if="record"
        :record="record"
        :mode="isAdminView ? 'admin' : 'teacher'"
        :score-loading="scoreLoading"
        :analysis-loading="analysisLoading"
        :review-saving="reviewSaving"
        :score-message="scoreMessage"
        :analysis-message="analysisMessage"
        :review-message="reviewMessage"
        @back="openNav('records')"
        @regenerate-score="regenerateScore"
        @run-analysis="runAnalysis"
        @save-review="saveTeacherReview"
      />
      <section v-else class="record-state-panel">
        <div class="section-kicker">{{ navRoleLabel }}</div>
        <h2>Record unavailable</h2>
        <p>The requested writing record could not be loaded.</p>
        <button class="ui-button ui-button--primary" type="button" @click="openNav('records')">Back to Writing Records</button>
      </section>
    </div>
  </AppShell>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../api'
import { useAuthStore } from '../stores/auth'
import AppShell from '../components/AppShell.vue'
import WritingRecordDetail from '../components/WritingRecordDetail.vue'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const record = ref(null)
const recordLoading = ref(true)
const recordError = ref('')
const analysis = ref(null)
const analysisLoading = ref(false)
const analysisMessage = ref('')
const scoreLoading = ref(false)
const scoreMessage = ref('')
const reviewSaving = ref(false)
const reviewMessage = ref('')
const mobileViewport = window.matchMedia('(max-width: 767px)')
const navCollapsed = ref(window.matchMedia('(max-width: 767px)').matches)

const teacherNavItems = [
  { view: 'overview', label: 'Overview', paths: ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M14 14h7v7h-7z', 'M3 14h7v7H3z'] },
  { view: 'classes', label: 'Programme/Class', paths: ['M6 3h12v18H6z', 'M9 7h6', 'M9 12h6', 'M9 17h4'] },
  { view: 'records', label: 'Writing Records', paths: ['M6 3h9l3 3v18H6z', 'M14 3v6h6', 'M9 13h6', 'M9 17h8'] },
  { view: 'students', label: 'Students', paths: ['M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M17 10a3 3 0 1 0 0-6', 'M2 21a7 7 0 0 1 14 0', 'M15 21a5 5 0 0 1 7 0'] },
  { view: 'profile', label: 'Profile', paths: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4 22a8 8 0 0 1 16 0'] },
]
const adminNavItems = [
  { view: 'overview', label: 'Overview', paths: ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M14 14h7v7h-7z', 'M3 14h7v7H3z'] },
  { view: 'records', label: 'Writing Records', paths: ['M6 3h9l3 3v18H6z', 'M14 3v6h6', 'M9 13h6', 'M9 17h8'] },
  { view: 'users', label: 'Users', paths: ['M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M17 10a3 3 0 1 0 0-6', 'M2 21a7 7 0 0 1 14 0', 'M15 21a5 5 0 0 1 7 0'] },
  { view: 'assignmentBank', label: 'Assignment Bank', paths: ['M4 5.5A2.5 2.5 0 0 1 6.5 3H20v17H6.5A2.5 2.5 0 0 0 4 22z', 'M4 5.5v16', 'M8 7h8', 'M8 11h6'] },
  { view: 'classes', label: 'Programme/Class', paths: ['M6 3h12v18H6z', 'M9 7h6', 'M9 12h6', 'M9 17h4'] },
  { view: 'profile', label: 'Profile', paths: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4 22a8 8 0 0 1 16 0'] },
]

const isAdminView = computed(() => auth.role === 'admin' || route.path.startsWith('/admin'))
const navItems = computed(() => isAdminView.value ? adminNavItems : teacherNavItems)
const navRoleLabel = computed(() => isAdminView.value ? 'Admin' : 'Teacher')

onMounted(() => {
  mobileViewport.addEventListener('change', syncNavigationForViewport)
  loadRecord()
})

onBeforeUnmount(() => {
  mobileViewport.removeEventListener('change', syncNavigationForViewport)
})

function syncNavigationForViewport(event) {
  navCollapsed.value = event.matches
}

function openNav(view) {
  router.push({ path: isAdminView.value ? '/admin' : '/teacher', query: { view } })
}

async function signOut() {
  await auth.logout()
  router.replace('/login')
}

async function loadRecord() {
  recordLoading.value = true
  recordError.value = ''
  try {
    const res = await apiFetch(`teacher/records/${route.params.recordId}`)
    const data = await readApiJson(res)
    if (!res.ok) throw new Error(data.error || `Request failed with status ${res.status}.`)
    if (!data.record) throw new Error('The requested writing record could not be found.')
    record.value = data.record
  } catch (error) {
    record.value = null
    recordError.value = error.message || 'The requested writing record could not be loaded.'
  } finally {
    recordLoading.value = false
  }
}

async function regenerateScore() {
  scoreLoading.value = true
  scoreMessage.value = ''
  try {
    const res = await apiFetch(`teacher/records/${route.params.recordId}/rescore`, { method: 'POST' })
    const data = await readApiJson(res)
    if (!res.ok) throw new Error(data.error || 'Could not regenerate score.')
    record.value = data.record
    scoreMessage.value = 'AI score regenerated.'
  } catch (error) {
    scoreMessage.value = error.message || 'Could not regenerate score.'
  } finally {
    scoreLoading.value = false
  }
}

async function saveTeacherReview(payload) {
  reviewSaving.value = true
  reviewMessage.value = ''
  try {
    const res = await apiFetch(`teacher/records/${route.params.recordId}/review`, {
      method: 'POST',
      body: JSON.stringify({
        teacher_score: payload.score,
        teacher_feedback: payload.feedback,
      }),
    })
    const data = await readApiJson(res)
    if (!res.ok) throw new Error(data.error || 'Could not save teacher review.')
    record.value = data.record
    reviewMessage.value = 'Review saved.'
  } catch (error) {
    reviewMessage.value = error.message || 'Could not save teacher review.'
  } finally {
    reviewSaving.value = false
  }
}

async function runAnalysis() {
  analysisLoading.value = true
  analysisMessage.value = ''
  try {
    const res = await apiFetch(`teacher/records/${route.params.recordId}/analysis`, { method: 'POST' })
    const data = await readApiJson(res)
    if (!res.ok) throw new Error(data.error || 'Could not generate chat analysis.')
    analysis.value = data.analysis
    record.value = { ...record.value, latest_analysis: data.analysis }
  } catch (error) {
    analysisMessage.value = error.message || 'Could not generate chat analysis.'
  } finally {
    analysisLoading.value = false
  }
}

async function readApiJson(response) {
  const text = await response.text()
  try {
    return text ? JSON.parse(text) : {}
  } catch {
    return { error: response.ok ? 'Unexpected empty response.' : `Request failed with status ${response.status}.` }
  }
}

function renderDraftContent(content = '') {
  const raw = String(content)
  if (/<\/?[a-z][\s\S]*>/i.test(raw)) return raw
  return escapeHtml(raw).replace(/\n/g, '<br>')
}

function formatAssistantMessage(content = '') {
  const escaped = escapeHtml(content)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(?:^|\n)- (.+)/g, '<br><span class="assistant-bullet">$1</span>')
    .replace(/\n/g, '<br>')
  return escaped.replace(/^<br>/, '')
}

function isToolMessage(message) {
  return ['polish', 'references', 'feedback'].includes(message.mode) || Boolean(message.tool || message.targetSentence)
}

function toolMessageLabel(message) {
  return message.tool || (message.mode === 'references' ? 'find reference' : message.mode === 'polish' ? 'improve writing' : 'general feedback')
}

function toolMessageNote(message) {
  if (message.mode === 'references') return message.targetSentence ? 'Finding references for this selected text.' : 'Finding references for the full draft.'
  if (message.mode === 'polish') return 'Improving the selected writing.'
  if (message.mode === 'feedback') return 'Giving feedback on the full draft.'
  if (message.tool === 'selected chat') return message.content
  return 'Using the selected text as context.'
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

const analysisDimensionNames = [
  'Tool Understanding and Operation',
  'Rhetorical Task Adaptation',
  'Critical Evaluation and Revision',
  'Ethical and Responsible Use',
  'Learning Reflection and Metacognition',
]

function analysisScore(value) {
  return Math.max(1, Math.min(5, Number(value || 1)))
}

function analysisMeterWidth(score) {
  return `${(analysisScore(score) / 5) * 100}%`
}

function normaliseAnalysis(value) {
  if (!value) return null
  let payload = value
  if (typeof value === 'string') {
    try {
      payload = JSON.parse(value)
    } catch {
      payload = parseLegacyAnalysis(value)
    }
  }
  const byName = Object.fromEntries((payload.dimensions || []).map(item => [item.name, item]))
  const dimensions = analysisDimensionNames.map(name => ({
    name,
    score: analysisScore(byName[name]?.score),
    comment: byName[name]?.comment || '',
  }))
  return { dimensions, summary: payload.summary || '' }
}

function parseLegacyAnalysis(text = '') {
  const names = analysisDimensionNames
  const dimensions = names.map((name, index) => {
    const nextNames = names.slice(index + 1).join('|') || 'Teaching Suggestions'
    const pattern = new RegExp(`\\*\\*${name}:\\s*([1-5])\\*\\*\\s*([\\s\\S]*?)(?=\\n\\s*\\*\\*(?:${nextNames}|Teaching Suggestions):|$)`)
    const match = String(text).match(pattern)
    return {
      name,
      score: match ? analysisScore(match[1]) : 1,
      comment: match ? match[2].trim() : '',
    }
  })
  const withoutSuggestions = String(text).split(/\n\s*\*\*Teaching Suggestions:\*\*/)[0]
  const summary = withoutSuggestions
    .replace(/\*\*(Tool Understanding and Operation|Rhetorical Task Adaptation|Critical Evaluation and Revision|Ethical and Responsible Use|Learning Reflection and Metacognition):\s*[1-5]\*\*[\s\S]*?(?=\n\s*\*\*(?:Tool Understanding and Operation|Rhetorical Task Adaptation|Critical Evaluation and Revision|Ethical and Responsible Use|Learning Reflection and Metacognition):|$)/g, '')
    .trim()
  return { dimensions, summary: summary || withoutSuggestions.trim() }
}
</script>

<style scoped>
.record-workspace {
  min-width: 0;
}

.record-state-panel {
  max-width: 45rem;
  margin: 0 auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  padding: var(--space-6);
  background: var(--surface);
}

.record-state-panel h2 {
  margin: 0 0 var(--space-2);
  font-family: var(--font-academic);
  font-weight: 600;
}

.record-state-panel p {
  margin: 0 0 var(--space-4);
  color: var(--ink-muted);
  line-height: 1.6;
}

.section-kicker {
  margin-bottom: var(--space-2);
  color: var(--accent-strong);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
</style>
